import {
  Archive16Regular,
  Archive20Regular,
  // Flag24Regular,
  Archive24Regular,
  ArrowReply16Filled,
  ArrowReply20Filled,
  // DocumentBulletList24Regular,
  Attach24Regular,
  AttachText24Regular,
  // ChevronDown16Filled,
  // Drafts24Regular,
  Delete24Regular,
  // Grid16Regular,
  // Grid24Regular,
  MailUnread20Regular,
  MailUnread24Regular,
  Send24Regular
} from "@fluentui/react-icons";

import { SCROLL_DELAY_MS } from "common/consts/scrollDelay";
import { useIsMobile } from "hooks/use-is-mobile";
import { usePermission } from "hooks/use-permissions";
import { Button } from "components/button/Button";
import { Tooltip } from "components/tooltip/Tooltip";
import { ThreadCategory } from "models/inbox/inbox";
import { useRootStore } from "store";
import CategorySelector from "./CategorySelector";

import "./ThreadActionBar.scss";

interface ThreadActionBarProps {
  mobile?: boolean;
  // Viewing mode props (optional when isReplying=true)
  onReply?: () => void;
  onMarkAsUnread?: () => void;
  showMarkAsUnread?: boolean;
  subscriber?: boolean;
  onCategoryChange?: (category: ThreadCategory) => void;
  onFlagClick?: () => void;
  onArchiveClick?: () => void;
  onUndoArchiveClick?: () => void;
  onStatsClick?: () => void;
  // Reply mode props
  isReplying?: boolean;
  onSendReply?: () => void;
  onAttach?: () => void;
  onAttachFromArchive?: () => void;
  onSaveDraft?: () => void;
  onDiscardDraft?: () => void;
  canSend?: boolean;
  isSending?: boolean;
  // Create new thread mode (mobile only)
  isCreatingNewThread?: boolean;
  selectedCategory?: ThreadCategory;
}

export default function ThreadActionBar({
  onReply,
  onMarkAsUnread,
  showMarkAsUnread,
  subscriber,
  mobile = false,
  onCategoryChange,
  onFlagClick,
  onArchiveClick,
  onUndoArchiveClick,
  onStatsClick,
  // Reply mode
  isReplying = false,
  onSendReply,
  onAttach,
  onAttachFromArchive,
  onSaveDraft,
  onDiscardDraft,
  canSend = false,
  isSending = false,
  // Create new thread mode
  isCreatingNewThread = false,
  selectedCategory
}: ThreadActionBarProps) {
  const { activeFilters, currentThread } = useRootStore((state) => state.inbox);
  const isInternalUser = usePermission("isInternal");

  const currentCategory =
    selectedCategory ?? currentThread?.category ?? ThreadCategory.None;
  const isMobile = useIsMobile();

  const handleOnReply = () => {
    if (onReply) {
      onReply();
      setTimeout(() => {
        const textEditorSelector = isMobile ? ".editor" : ".create-wrapper";
        const textEditor = document.querySelector(textEditorSelector);
        const threadWrapper = document.querySelector(".thread-wrapper");
        if (!textEditor) {
          return;
        }
        if (isMobile) {
          textEditor.scrollIntoView({
            behavior: "smooth",
            block: "start",
            inline: "nearest"
          });
        } else {
          if (threadWrapper) {
            const editorRect = textEditor.getBoundingClientRect();
            const wrapperRect = threadWrapper.getBoundingClientRect();
            const scrollTop =
              threadWrapper.scrollTop + (editorRect.top - wrapperRect.top);
            threadWrapper.scrollTo({
              top: scrollTop,
              behavior: "smooth"
            });
          }
        }
      }, SCROLL_DELAY_MS);
    }
  };

  const showArchive =
    isInternalUser && activeFilters.archived && currentThread?.isArchived;

  if (mobile) {
    // Reply mode: Show send button and reply actions
    if (isReplying) {
      return (
        <div className="thread-action-bar-mobile reply-mode no-print">
          {/* Send Button */}
          <Tooltip content={<p className="m-0">Skicka</p>}>
            <button
              className="send-button"
              onClick={onSendReply}
              disabled={!canSend || isSending}
              aria-label="Skicka"
            >
              <span className="send-text">Skicka</span>
              <Send24Regular className="send-icon" />
            </button>
          </Tooltip>

          {/* Reply Action Icons */}
          <div className="action-icons">
            {/* Category Selector for creating new threads on mobile */}
            {isCreatingNewThread && onCategoryChange && isInternalUser && (
              <CategorySelector
                currentCategory={currentCategory}
                onCategoryChange={onCategoryChange}
                mobile={true}
                disabled={false}
              />
            )}
            {/* {onSaveDraft && (
              <button
                className="icon-button"
                onClick={onSaveDraft}
                title="Spara utkast"
              >
                <Drafts24Regular />
              </button>
            )} */}
            {onAttachFromArchive && (
              <Tooltip content={<p className="m-0">Bifoga från arkiv</p>}>
                <button
                  className="icon-button"
                  onClick={onAttachFromArchive}
                  aria-label="Bifoga från arkiv"
                >
                  <AttachText24Regular
                    style={{
                      width: "32px",
                      height: "32px"
                    }}
                  />
                </button>
              </Tooltip>
            )}
            {onAttach && (
              <Tooltip content={<p className="m-0">Bifoga fil</p>}>
                <button
                  className="icon-button"
                  onClick={onAttach}
                  aria-label="Bifoga fil"
                >
                  <Attach24Regular />
                </button>
              </Tooltip>
            )}
            {onDiscardDraft && (
              <Tooltip content={<p className="m-0">Släng utkast</p>}>
                <button
                  className="icon-button delete-button"
                  onClick={onDiscardDraft}
                  aria-label="Släng utkast"
                >
                  <Delete24Regular />
                </button>
              </Tooltip>
            )}
          </div>
        </div>
      );
    }

    // Only for archived thread
    if (showArchive) {
      return (
        <div className="thread-action-bar-mobile justify-content-end no-print">
          {onUndoArchiveClick && (
            <Button
              size="sm"
              color="tertiary"
              variant="outline"
              className="ml-2"
              onClick={onUndoArchiveClick}
            >
              <div className="d-flex align-items-center">
                <span className="mr-2">Återställ från arkiv</span>
                <Archive16Regular />
              </div>
            </Button>
          )}
        </div>
      );
    }

    // Default mode: Show reply button and thread actions
    return (
      <div className="thread-action-bar-mobile no-print">
        {/* Reply Button */}
        {onReply && (
          <div className="reply-button-container">
            <button className="reply-button" onClick={handleOnReply}>
              <ArrowReply20Filled className="reply-icon" />
              <span className="reply-text">Svara</span>
            </button>
          </div>
        )}

        {/* Category and Actions */}
        <div className="action-icons">
          {onCategoryChange && isInternalUser && (
            <CategorySelector
              currentCategory={currentCategory}
              onCategoryChange={onCategoryChange}
              mobile={true}
            />
          )}
          {/* {onFlagClick && (
            <button className="icon-button" onClick={onFlagClick}>
              <Flag24Regular />
            </button>
          )} */}
          {subscriber && onMarkAsUnread && !activeFilters.archived && (
            <Tooltip
              content={
                isReplying ? (
                  <span>Kan inte markera som oläst när du svarar</span>
                ) : !showMarkAsUnread ? (
                  <span>Meddelandet är redan markerat som oläst</span>
                ) : (
                  <span>Markera som oläst</span>
                )
              }
            >
              <Button
                variant="round transparent"
                size="action"
                onClick={onMarkAsUnread}
                disabled={isReplying || !showMarkAsUnread}
              >
                <MailUnread24Regular />
              </Button>
            </Tooltip>
          )}
          {onArchiveClick && isInternalUser && (
            <button
              className="icon-button"
              onClick={onArchiveClick}
              title="Arkivera"
            >
              <Archive24Regular />
            </button>
          )}
          {/* {onStatsClick && (
            <button className="icon-button" onClick={onStatsClick}>
              <DocumentBulletList24Regular />
            </button>
          )} */}
        </div>
      </div>
    );
  }

  // Desktop view (original)
  return (
    <div className="d-flex align-items-center mb-3 no-print">
      {/* Reply */}
      {onReply && !activeFilters.archived && (
        <Tooltip content={<p className="m-0">Svara</p>}>
          <Button
            size="sm"
            className="d-flex text-nowrap mr-2"
            onClick={handleOnReply}
            disabled={isReplying}
            aria-label="Svara"
          >
            <ArrowReply16Filled className="mr-2" />
            Svara
          </Button>
        </Tooltip>
      )}

      {/* Category */}
      {!activeFilters.archived && onCategoryChange && isInternalUser && (
        <Tooltip content={<p className="m-0">Välj kategori</p>}>
          <CategorySelector
            currentCategory={currentCategory}
            onCategoryChange={onCategoryChange}
            mobile={false}
          />
        </Tooltip>
      )}

      {/* Mark as unread */}
      {subscriber && onMarkAsUnread && !activeFilters.archived && (
        <Tooltip
          content={
            isReplying || !showMarkAsUnread ? (
              <span>Meddelandet är redan markerat som oläst</span>
            ) : (
              <span>Markera som oläst</span>
            )
          }
        >
          <Button
            variant="round transparent"
            size="action"
            onClick={onMarkAsUnread}
            disabled={isReplying || !showMarkAsUnread}
          >
            <MailUnread20Regular />
          </Button>
        </Tooltip>
      )}
      {/* Archive thread */}
      {onArchiveClick && isInternalUser && !activeFilters.archived && (
        <Tooltip content={<span>Arkivera</span>}>
          <Button
            size="action"
            color="tertiary"
            variant="round transparent"
            className="ml-2"
            onClick={onArchiveClick}
            disabled={isReplying}
          >
            <Archive20Regular />
          </Button>
        </Tooltip>
      )}
      {/*Undo Archive thread */}
      {onUndoArchiveClick && showArchive && (
        <Button
          size="sm"
          color="tertiary"
          variant="outline"
          className="ml-2"
          onClick={onUndoArchiveClick}
        >
          <div className="d-flex align-items-center">
            <span className="mr-2">Återställ från arkiv</span>
            <Archive16Regular />
          </div>
        </Button>
      )}
    </div>
  );
}
